import { useCallback, useEffect, useState } from 'react';

import { useLocalSearchParams } from 'expo-router';

import { ShortCodeState } from 'state/loyalty/in-restaurant-redemption';
import logger from 'utils/logger';

export interface IUserRedemptionCode {
  shortCode?: string;
  shortCodeState: ShortCodeState;
  isShortCodeLoading: boolean;
}

export const useWhopperGuaranteeRedemptionCode = (): IUserRedemptionCode => {
  // Extract guaranteeId from URL path (e.g., /whopper-guarantee/ABC12345678)
  // This ID identifies the specific Whopper Guarantee offer to redeem
  const { guaranteeId } = useLocalSearchParams<{ guaranteeId: string }>();

  const [shortCode, setShortCode] = useState<string | undefined>(undefined);
  const [shortCodeState, setShortCodeState] = useState<ShortCodeState>(ShortCodeState.None);
  const [isShortCodeLoading, setIsShortCodeLoading] = useState<boolean>(false);

  const fetchRedemptionCode = useCallback(async () => {
    if (!guaranteeId) {
      logger.warn('No guarantee ID provided');
      setShortCodeState(ShortCodeState.RequestError);
      return;
    }

    setIsShortCodeLoading(true);
    setShortCodeState(ShortCodeState.None);

    try {
      // TODO: BKPE-23692 Replace with actual API call when endpoint is available
      // Example API call structure:
      // const response = await fetch(`/api/whopper-guarantee/${guaranteeId}/redeem`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      // });
      // const data = await response.json();
      // setShortCode(data.redemptionCode);

      // Temporary mock implementation - simulates API delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Mock 6-digit code generation for development
      // This should be replaced with actual API response
      const mockCode = Math.floor(100000 + Math.random() * 900000).toString();

      setShortCode(mockCode);
      setShortCodeState(ShortCodeState.Pending);

      logger.info(`Fetched redemption code for guarantee ID: ${guaranteeId}`);
    } catch (error) {
      logger.error({
        message: `Error fetching whopper guarantee redemption code for guarantee ID: ${guaranteeId}`,
        context: { guaranteeId },
        error,
      });
      setShortCodeState(ShortCodeState.RequestError);
      setShortCode(undefined);
    } finally {
      setIsShortCodeLoading(false);
    }
  }, [guaranteeId]);

  // Auto-fetch code when guarantee ID is available
  useEffect(() => {
    if (guaranteeId && shortCodeState === ShortCodeState.None && !isShortCodeLoading) {
      fetchRedemptionCode();
    }
  }, [guaranteeId, shortCodeState, isShortCodeLoading, fetchRedemptionCode]);

  return {
    shortCode,
    shortCodeState,
    isShortCodeLoading,
  };
};
